import { useLayoutEffect } from "react";

interface UseScrollRestorationOptions {
  /**
   * Key to uniquely identify the scroll position in sessionStorage.
   */
  key: string;
  containerId: string;
  /**
   * Whether to save the scroll position when the component unmounts.
   * Default is true.
   */
  saveOnUnmount?: boolean;
}

/**
 * Saves the scroll position of the specified container.
 */
export function saveScrollPosition(key: string, containerId: string): void {
  const scrollPosition = document.querySelector(`#${containerId}`)?.scrollTop ?? 0;
  sessionStorage.setItem(key, scrollPosition.toString());
}

/**
 * Restores the scroll position of the specified container.
 */
export function restoreScrollPosition(key: string, containerId: string): void {
  const savedPosition = sessionStorage.getItem(key);
  if (savedPosition) {
    const scrollToContainer = document.querySelector(`#${containerId}`);
    if (scrollToContainer && typeof scrollToContainer.scrollTo === "function") {
      scrollToContainer.scrollTo(0, Number.parseInt(savedPosition, 10));
    }

    sessionStorage.removeItem(key);
  }
}

/**
 * Clears the scroll position for a given key.
 */
export function clearScrollPosition(key: string): void {
  sessionStorage.removeItem(key);
}

/**
 * A hook to preserve and restore the scroll position of a page.
 */
export function useScrollRestoration(options: UseScrollRestorationOptions): void {
  const { key, containerId, saveOnUnmount = true } = options;

  useLayoutEffect(() => {
    restoreScrollPosition(key, containerId);

    return () => {
      if (saveOnUnmount) {
        saveScrollPosition(key, containerId);
      }
    };
  }, [key, containerId, saveOnUnmount]);
}
